import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePermission } from '@/components/guards/permission-guard';
import {
  ArrowLeft,
  Boxes,
  Loader2,
  Pencil,
  Package,
  Tag,
  FileText,
  MoreHorizontal,
  Eye,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { ModelModal } from '@/components/trading/model-modal';
import { trpc } from '@/lib/trpc';
import { cn } from '@/lib/utils';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';

export function ModelDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const canEdit = usePermission('MODELS', 'EDIT');
  const canDelete = usePermission('MODELS', 'DELETE');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<string | null>(null);

  const { data: model, isLoading, error } = trpc.tradingModel.byId.useQuery(
    id!,
    { enabled: !!id }
  );

  const utils = trpc.useUtils();

  const deleteProductMutation = trpc.tradingProduct.delete.useMutation({
    onSuccess: () => {
      utils.tradingModel.byId.invalidate(id!);
      showSuccessToast(t('common.deleted'));
      setDeleteConfirmOpen(false);
      setProductToDelete(null);
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const handleDeleteProduct = (productId: string) => {
    setProductToDelete(productId);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteProduct = () => {
    if (productToDelete) {
      deleteProductMutation.mutate(productToDelete);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2 text-muted-foreground">{tCommon('loading')}</span>
      </div>
    );
  }

  if (error || !model) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate('/trading/models')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('models.backToList')}
        </Button>
        <div className="border border-red-300 bg-red-50 p-4 text-red-700">
          <p className="font-medium">{tCommon('error')}</p>
          <p className="text-sm">{error?.message || t('models.notFound')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/trading/models')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('models.backToList')}
          </Button>
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center bg-gray-100">
              {model.image ? (
                <img
                  src={model.image}
                  alt={model.name}
                  className="h-full w-full object-contain"
                />
              ) : (
                <Boxes className="h-6 w-6 text-gray-400" />
              )}
            </div>
            <div>
              <h1 className="text-2xl font-bold">{model.name}</h1>
              <p className="text-muted-foreground">
                <span className="font-mono text-sm">{model.brand.code}.{model.code}</span>
                {' - '}
                <Link
                  to={`/trading/brands`}
                  className="text-primary hover:underline"
                >
                  {model.brand.name}
                </Link>
              </p>
            </div>
          </div>
        </div>
        {canEdit && (
          <Button onClick={() => setIsModalOpen(true)}>
            <Pencil className="mr-2 h-4 w-4" />
            {tCommon('edit')}
          </Button>
        )}
      </div>

      <ModelModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        model={model}
      />

      <ConfirmationDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title={tCommon('delete')}
        description={t('products.deleteConfirm')}
        confirmLabel={tCommon('delete')}
        variant="destructive"
        onConfirm={confirmDeleteProduct}
        isLoading={deleteProductMutation.isPending}
      />

      <div className="grid gap-6 md:grid-cols-3">
        {/* Model Info Card */}
        <Card className="md:col-span-1">
          <CardHeader>
            <CardTitle className="text-base">{t('models.modelInfo')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">{t('models.statusLabel')}</span>
              <span
                className={cn(
                  'px-2 py-0.5 text-xs font-medium',
                  model.isActive
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-700'
                )}
              >
                {model.isActive ? t('models.active') : t('models.inactive')}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">{t('models.fullCode')}</span>
              <span className="font-mono text-sm">{model.brand.code}.{model.code}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">{t('models.brand')}</span>
              <span className="bg-emerald-100 px-1.5 py-0.5 text-xs font-medium text-emerald-700">
                {model.brand.name}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">{t('models.slug')}</span>
              <span className="font-mono text-xs text-muted-foreground">{model.slug}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">{t('models.sortOrder')}</span>
              <span>{model.sortOrder}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">{t('models.products')}</span>
              <span className="font-medium">{model.products.length}</span>
            </div>
          </CardContent>
        </Card>

        {/* Description Card */}
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <FileText className="h-4 w-4" />
              {t('models.description')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {model.description ? (
              <p className="text-sm whitespace-pre-wrap">{model.description}</p>
            ) : (
              <p className="text-sm text-muted-foreground italic">
                {t('models.noDescription')}
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Products Section */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              {t('models.productsUnderModel')}
            </CardTitle>
            <CardDescription>
              {model.products.length} {t('models.products').toLowerCase()}
            </CardDescription>
          </div>
          {canEdit && (
            <Button
              variant="outline"
              onClick={() => navigate(`/trading/products?modelId=${model.id}`)}
            >
              {t('models.viewAllProducts')}
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {model.products.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <Package className="h-10 w-10 text-muted-foreground/50" />
              <h3 className="mt-3 text-sm font-medium">{t('products.noProducts')}</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {t('products.noProductsDesc')}
              </p>
            </div>
          ) : (
            <div className="border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr className="border-b border-border">
                    <th className="px-3 py-2 text-left font-medium">{t('products.image')}</th>
                    <th className="px-3 py-2 text-left font-medium">{t('products.fullCode')}</th>
                    <th className="px-3 py-2 text-left font-medium">{t('products.name')}</th>
                    <th className="px-3 py-2 text-left font-medium">{t('products.status')}</th>
                    <th className="px-3 py-2 text-center font-medium w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {model.products.map((product) => (
                    <tr
                      key={product.id}
                      className="border-b border-border last:border-b-0 hover:bg-muted/30 cursor-pointer"
                      onClick={() => navigate(`/trading/products/${product.id}`)}
                    >
                      <td className="px-3 py-2">
                        <div className="h-10 w-10 bg-gray-100 flex items-center justify-center">
                          {product.images?.[0]?.url ? (
                            <img
                              src={product.images[0].url}
                              alt={product.name}
                              className="h-full w-full object-contain"
                              referrerPolicy="no-referrer"
                              loading="lazy"
                            />
                          ) : (
                            <Tag className="h-5 w-5 text-gray-400" />
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <span className="font-mono text-xs">
                          {model.brand.code}.{model.code}.{product.code}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-medium">{product.name}</td>
                      <td className="px-3 py-2">
                        <span
                          className={cn(
                            'px-1.5 py-0.5 text-xs font-medium',
                            product.isActive
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-700'
                          )}
                        >
                          {product.isActive ? t('products.active') : t('products.inactive')}
                        </span>
                      </td>
                      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-7 w-7">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/trading/products/${product.id}`)}>
                              <Eye className="mr-2 h-4 w-4" />
                              {tCommon('view')}
                            </DropdownMenuItem>
                            {canEdit && (
                              <DropdownMenuItem onClick={() => navigate(`/trading/products/${product.id}`)}>
                                <Pencil className="mr-2 h-4 w-4" />
                                {tCommon('edit')}
                              </DropdownMenuItem>
                            )}
                            {canDelete && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-red-600"
                                  onClick={() => handleDeleteProduct(product.id)}
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  {tCommon('delete')}
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default ModelDetailPage;
